import { OWNER_STAFF_ID, salonDayFor } from './salonDays'
import type { AgendaAppointment } from '../types/agenda'
import type { Sale } from '../types/kassa'
import type { OpeningDay, SalonDayException } from '../types/site'

/**
 * Dagsamenvatting na een dag van een collega, voor Sarahs overzicht (docs/KASSA-ABONNEMENTEN.md
 * §3b). Puur. Telt alleen haar afspraken en bonnen; "nog open" = afgerond zonder bon (salon) en
 * aanvragen voor haar dagen vanaf vandaag.
 */
export type StaffDaySummary = {
  staffId: string
  date: string
  /** Klanten die er waren (afgerond). */
  clients: number
  noShows: number
  cancelled: number
  /** Verkoop op haar bonnen die dag (betaald, niet gecorrigeerd), in centen. */
  salesCents: number
  /** Afgerond maar nog niet afgerekend. */
  unpaid: AgendaAppointment[]
  /** Open aanvragen voor haar, vanaf vandaag. */
  openRequests: AgendaAppointment[]
}

/** De laatste dag (tot en met `todayIso`, hooguit `lookback` dagen terug) waarop een collega werkte. */
export function lastColleagueDay(
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  todayIso: string,
  lookback = 7,
): { date: string; staffId: string } | null {
  const [y, m, d] = todayIso.split('-').map(Number)
  for (let i = 0; i <= lookback; i++) {
    const dt = new Date(y, m - 1, d - i, 12)
    const date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
    const day = salonDayFor(hours, exceptions, date)
    if (day && day.staffId !== OWNER_STAFF_ID) return { date, staffId: day.staffId }
  }
  return null
}

export function staffDaySummary(
  appointments: readonly AgendaAppointment[],
  sales: readonly Pick<Sale, 'staffId' | 'status' | 'totalCents' | 'day'>[],
  staffId: string,
  date: string,
  todayIso: string,
): StaffDaySummary {
  const hers = appointments.filter((a) => (a.staffId || OWNER_STAFF_ID) === staffId)
  const ofDay = hers.filter((a) => a.date === date)
  return {
    staffId,
    date,
    clients: ofDay.filter((a) => a.outcome === 'afgerond').length,
    noShows: ofDay.filter((a) => a.outcome === 'no-show').length,
    cancelled: ofDay.filter((a) => a.outcome === 'geannuleerd').length,
    salesCents: sales.filter((s) => s.day === date && (s.staffId || OWNER_STAFF_ID) === staffId && s.status === 'betaald').reduce((n, s) => n + s.totalCents, 0),
    unpaid: ofDay.filter((a) => a.outcome === 'afgerond' && a.kind === 'salon' && !a.saleId),
    openRequests: hers.filter((a) => a.status === 'aanvraag' && !a.outcome && a.date >= todayIso),
  }
}
